import React, { useState, useEffect, useMemo } from "react";
import "./Report.css";
import "../client/Client.css";
import config from "../../config";
import Header from "../../components/common/Header";
import { BiExport } from "react-icons/bi";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

const Report = ({ setActivePage }) => {
  const [selectedClientId, setSelectedClientId] = useState("");
  const [clientList, setClientList] = useState([]);
  const [jobRoleList, setJobRoleList] = useState([]);
  const [selectedJobRole, setSelectedJobRole] = useState("");
  const [month, setMonth] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [error, setError] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 9;

  const [userDetails, setUserDetails] = useState({
    name: "", role: "", token: "", orgId: "",
  });

  const months = [
    "JANUARY", "FEBRUARY", "MARCH", "APRIL", "MAY", "JUNE",
    "JULY", "AUGUST", "SEPTEMBER", "OCTOBER", "NOVEMBER", "DECEMBER",
  ];
  const monthLabels = {
    JANUARY: "January", FEBRUARY: "February", MARCH: "March", APRIL: "April",
    MAY: "May", JUNE: "June", JULY: "July", AUGUST: "August",
    SEPTEMBER: "September", OCTOBER: "October", NOVEMBER: "November", DECEMBER: "December",
  };

  // ── Load user details ──────────────────────────────────────────
  useEffect(() => {
    const token = localStorage.getItem("Authorization") || "";
    const firstName = localStorage.getItem("firstName") || "";
    const lastName = localStorage.getItem("lastName") || "";
    const role = localStorage.getItem("role")?.toUpperCase() || "GUEST";
    const orgId = localStorage.getItem("orgId") || "";
    setUserDetails({ name: `${firstName} ${lastName}`.trim(), role, token, orgId });
  }, []);

  // ── Fetch clients ──────────────────────────────────────────────
  useEffect(() => {
    if (!userDetails.orgId || !userDetails.token) return;
    fetch(`${config.API_BASE_URL}${config.CLIENT_LIST_ENDPOINT}${userDetails.orgId}`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${userDetails.token}`,
        "Content-Type": "application/json",
      },
    })
      .then((res) => res.json())
      .then((data) => {
        const list = Array.isArray(data.body) ? data.body : [];
        setClientList(list);
        if (list.length > 0 && !selectedClientId) {
          setSelectedClientId(list[0].clientKey);
        }
      })
      .catch((e) => console.error("Client fetch error:", e));
  }, [userDetails.orgId, userDetails.token, selectedClientId]);

  // ── Fetch Job Roles ────────────────────────────────────────────
  useEffect(() => {
    if (!userDetails.orgId || !userDetails.token) return;
    fetch(
      `${config.API_BASE_URL}${config.VIEW_CLIENT_CONFIG_ENDPOINT}${userDetails.orgId}?configType=JobRole`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${userDetails.token}`,
          "Content-Type": "application/json",
        },
      }
    )
      .then((res) => res.json())
      .then((data) => setJobRoleList(Array.isArray(data) ? data : (data.body || [])))
      .catch((e) => console.error("Job Role fetch error:", e));
  }, [userDetails.orgId, userDetails.token]);

  // ── Fetch ESI/PF Report (triggered by Go button) ──────────────
  const fetchEsiPfReport = async () => {
    if (!userDetails.token || !userDetails.orgId) return;

    setLoading(true);
    setError(null);
    setReportData([]);

    try {
      // Build query params in the documented order
      const params = new URLSearchParams();
      params.set("orgKey", userDetails.orgId);
      if (selectedClientId) params.set("clientKey", selectedClientId);
      if (selectedJobRole) params.set("jobRole", selectedJobRole);
      if (month) params.set("month", month);
      // year derived from fromDate if provided, else current year
      const yr = fromDate ? fromDate.split("-")[0] : new Date().getFullYear();
      params.set("year", yr);
      if (fromDate) params.set("fromDate", fromDate);
      if (toDate) params.set("toDate", toDate);

      const url = `${config.API_BASE_URL}${config.ESI_PF_REPORT_ENDPOINT}?${params.toString()}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${userDetails.token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) throw new Error(`API error: ${response.status} ${response.statusText}`);

      const data = await response.json();
      // Support both flat array and wrapped body
      const list = Array.isArray(data) ? data : (data.body ?? data.data ?? []);
      setReportData(list);
      setCurrentPage(1);
    } catch (err) {
      console.error("ESI/PF report error:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ── Search filter ──────────────────────────────────────────────
  const [search, setSearch] = useState("");
  const filteredData = useMemo(() => {
    if (!search.trim()) return reportData;
    const term = search.toLowerCase();
    return reportData.filter(
      (row) =>
        (row.employeeName || "").toLowerCase().includes(term) ||
        (row.employeeCode || "").toLowerCase().includes(term) ||
        (row.employeeId || "").toString().includes(term)
    );
  }, [reportData, search]);

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredData.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredData, currentPage]);

  // ── Export CSV ─────────────────────────────────────────────────
  const handleExportCSV = async () => {
    if (!userDetails.token || !userDetails.orgId) return;

    try {
      const params = new URLSearchParams();
      params.set("orgKey", userDetails.orgId);
      if (selectedClientId) params.set("clientKey", selectedClientId);
      if (selectedJobRole) params.set("jobRole", selectedJobRole);
      if (month) params.set("month", month);
      const yr = fromDate ? fromDate.split("-")[0] : new Date().getFullYear();
      params.set("year", yr);
      if (fromDate) params.set("fromDate", fromDate);
      if (toDate) params.set("toDate", toDate);

      const url = `${config.API_BASE_URL}${config.ESI_PF_REPORT_ENDPOINT}?${params.toString()}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${userDetails.token}`,
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
      });

      if (!response.ok) throw new Error(`API error: ${response.status} ${response.statusText}`);

      const data = await response.json();
      const list = Array.isArray(data) ? data : (data.body ?? data.data ?? []);

      if (list.length === 0) {
        alert("No data available to export.");
        return;
      }

      const workbook = new ExcelJS.Workbook();
      const sheet = workbook.addWorksheet("ESI Report");

      sheet.columns = [
        { header: "", key: "ipNumber", width: 18 },
        { header: "", key: "ipName", width: 32 },
        { header: "", key: "paidDays", width: 25 },
        { header: "", key: "totalWages", width: 20 },
        { header: "", key: "reasonCode", width: 45 },
        { header: "", key: "lastWorkingDay", width: 26 },
      ];

      const headerRow = sheet.getRow(1);
      headerRow.height = 70;

      const headerCells = [
        { main: "IP Number\n", sub: "(10 Digits)" },
        { main: "IP Name\n", sub: "( Only alphabets and space )" },
        { main: "No of Days for which wages paid/payable during the\nmonth", sub: "" },
        { main: "Total Monthly Wages", sub: "" },
        { main: "Reason Code for Zero workings\ndays(numeric only; provide 0 for\nall other reasons- Click on the link\nfor reference)", sub: "" },
        { main: "Last Working Day\n", sub: "( Format DD/MM/YYYY or DD-MM-YYYY)" }
      ];

      headerCells.forEach((h, idx) => {
        const cell = headerRow.getCell(idx + 1);
        if (h.sub) {
          cell.value = {
            richText: [
              { text: h.main, font: { bold: true, name: "Arial", size: 10 } },
              { text: h.sub, font: { bold: true, color: { argb: "FFFF0000" }, name: "Arial", size: 10 } }
            ]
          };
        } else {
          cell.value = h.main;
          cell.font = { bold: true, name: "Arial", size: 10 };
        }
        cell.alignment = { wrapText: true, vertical: "middle", horizontal: "center" };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFCCFFFF" }
        };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" }
        };
      });

      list.forEach((r) => {
        const ipNumber = r.ipNumber ?? r.esiNumber ?? r.employeeCode ?? r.employeeId ?? "";
        const ipName = r.employeeName ?? "";
        const paidDays = r.paidDays ?? r.payableDays ?? r.workingDays ?? "26";
        const totalWages = r.grossSalary ?? r.totalWages ?? r.basicSalary ?? r.monthlyWages ?? "";
        const reasonCode = r.reasonCode ?? "";
        const lastWorkingDay = r.lastWorkingDay ?? "";

        const row = sheet.addRow({
          ipNumber, ipName, paidDays, totalWages, reasonCode, lastWorkingDay
        });

        row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
          cell.font = { name: "Arial", size: 10 };
          if ([3, 4].includes(colNumber)) {
            cell.alignment = { vertical: "middle", horizontal: "center" };
          } else {
            cell.alignment = { vertical: "middle", horizontal: "left", wrapText: true };
          }
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" }
          };
        });
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      saveAs(blob, `ESI_Report_Template_${month}_${yr}.xlsx`);

    } catch (err) {
      console.error("ESI/PF export error:", err);
      alert("Failed to export report.");
    }
  };

  return (
    <div className="report-container">
      <Header
        title="ESI & PF"
        subtitle="ESI & PF Information"
        onProfileClick={() => setActivePage && setActivePage("Profile")}
      />

      <div className="table-container report-card" style={{ padding: "24px" }}>
        {/* ── Filters ── */}
        <div className="report-filters-section">

          {/* Client */}
          <div className="report-filter-item">
            <label className="report-filter-label">Client</label>
            <select
              className="report-filter-input"
              value={selectedClientId}
              onChange={(e) => setSelectedClientId(e.target.value)}
            >
              {clientList.map((client) => (
                <option key={client.clientKey} value={client.clientKey}>
                  {client.name}
                </option>
              ))}
            </select>
          </div>

          {/* Job Role */}
          <div className="report-filter-item">
            <label className="report-filter-label">Job Role</label>
            <select
              className="report-filter-input"
              value={selectedJobRole}
              onChange={(e) => setSelectedJobRole(e.target.value)}
            >
              <option value="">All</option>
              {jobRoleList.map((role) => (
                <option key={role.id} value={role.values}>
                  {role.values}
                </option>
              ))}
            </select>
          </div>

          {/* Month */}
          <div className="report-filter-item">
            <label className="report-filter-label">Month</label>
            <select
              className="report-filter-input"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
            >
              <option value="">All</option>
              {months.map((m) => (
                <option key={m} value={m}>{monthLabels[m]}</option>
              ))}
            </select>
          </div>

          {/* From Date */}
          <div className="report-filter-item">
            <label className="report-filter-label">From</label>
            <input
              type="date"
              className="report-filter-input"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </div>

          {/* To Date */}
          <div className="report-filter-item">
            <label className="report-filter-label">To</label>
            <input
              type="date"
              className="report-filter-input"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </div>

          {/* Buttons */}
          <div className="report-btn-group">
            <button className="report-btn-primary" onClick={fetchEsiPfReport}>
              Go
            </button>
            <button
              className="report-btn-primary"
              onClick={() => setShowExportModal(!showExportModal)}
            >
              <BiExport size={18} /> Export
            </button>

            {showExportModal && (
              <div className="report-export-modal">
                <label className="report-filter-label">ESI / PF</label>
                <select className="report-filter-input" style={{ marginBottom: "16px" }}>
                  <option>Combined</option>
                </select>
                <button onClick={handleExportCSV} className="report-btn-primary-full">
                  Download
                </button>
              </div>
            )}
          </div>
        </div>

        {/* ── Table ── */}
        <div className="report-table-wrapper">
          <table className={`table report-table-main ${paginatedData.length === itemsPerPage ? "report-table-fill" : ""}`}>
            <thead>
              <tr className="report-table-row">
                <th className="th">Employee Name</th>
                <th className="th">Employee ID</th>
                <th className="th">ESI %</th>
                <th className="th">PF %</th>
                <th className="th">ESI Amount</th>
                <th className="th">PF Amount</th>
                <th className="th">Month</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr className="report-table-row">
                  <td colSpan={7} className="report-empty-cell">Loading data...</td>
                </tr>
              ) : error ? (
                <tr className="report-table-row">
                  <td colSpan={7} className="report-empty-cell" style={{ color: "#ef4444" }}>
                    {error}
                  </td>
                </tr>
              ) : filteredData.length === 0 ? (
                <tr className="report-table-row">
                  <td colSpan={7} className="report-empty-cell">
                    {reportData.length === 0
                      ? "Select filters and click Go to load report"
                      : "No records found"}
                  </td>
                </tr>
              ) : (
                paginatedData.map((row, index) => (
                  <tr key={row.employeeId ?? index} className="report-table-row">
                    <td className="td">{row.employeeName ?? "—"}</td>
                    <td className="td">{row.employeeCode ?? row.employeeId ?? "—"}</td>
                    <td className="td">{row.esiPercentage ?? "0.75%"}</td>
                    <td className="td">{row.pfPercentage ?? "12%"}</td>
                    <td className="td">{row.esiAmount ?? row.esiContribution ?? "—"}</td>
                    <td className="td">{row.pfAmount ?? row.pfContribution ?? "—"}</td>
                    <td className="td">{row.month ? (monthLabels[row.month] ?? row.month) : (monthLabels[month] ?? month ?? "—")}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {filteredData.length > 0 && (
            <div className='table-footer'>
              <div className='record-count'>
                Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                {Math.min(currentPage * itemsPerPage, filteredData.length)} of{" "}
                {filteredData.length} entries
              </div>

              <div className='pagination'>
                <button
                  className='pagination-button'
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                >
                  &lt;
                </button>

                {Array.from(
                  { length: Math.ceil(filteredData.length / itemsPerPage) },
                  (_, i) => (
                    <button
                      key={i}
                      className={`pagination-button ${currentPage === i + 1 ? "active" : ""}`}
                      onClick={() => setCurrentPage(i + 1)}
                    >
                      {i + 1}
                    </button>
                  )
                )}

                <button
                  className='pagination-button'
                  disabled={currentPage === Math.ceil(filteredData.length / itemsPerPage)}
                  onClick={() => setCurrentPage(currentPage + 1)}
                >
                  &gt;
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Report;
